// ─── Node ID contract ───────────────────────────────────────────────────────
//
// Every TreeNode ID is a UUID v4 string generated by the client via
// crypto.randomUUID(). IDs must be globally unique across all pages, users,
// and projects in the application. The server trusts client-generated IDs and
// will reject duplicates (IdConflict result). Factory methods generate IDs
// internally — callers never supply an ID.
//
// In serialized HTML, each element's ID is written as data-rd-id="<uuid>".
// The iframe bridge queries document.querySelector('[data-rd-id="<uuid>"]')
// to find the real DOM node for atomic updates.

// ─── Versioning ─────────────────────────────────────────────────────────────
//
// The server manages two separate counters per property change:
//   version — property-level version counter used for LWW conflict resolution.
//             The client compares incoming.version > current.version to decide
//             whether to accept an update. Assigned by the server; clients send 0.
//   seq     — page-level sequence reference at the time this property changed.
//             Used for ordering and reconnect, not for LWW decisions.
//
// Clients always send version: 0 and seq: 0. The server ignores client values
// and assigns its own canonical version and seq on every accepted patch.
// LWW comparison only happens on the receive side at the client, to guard
// against delayed or out-of-order packet delivery.

export interface VersionedValue<T> {
    version: number;
    seq: number;
    value: T;
}

// ─── Node types ─────────────────────────────────────────────────────────────

export type TreeNodeType = "element" | "text" | "comment" | "document";

export interface TreeNode {
    id: string;
    type: TreeNodeType;
    parentId: string;
    // LexoRank string assigned by the server (e.g. "0|hzzzzz:").
    // Empty string until the server confirms the node's position.
    position: string;
    tagName: VersionedValue<string>;
    text: VersionedValue<string | null>;
    comment: VersionedValue<string | null>;
    // HTML attributes. The "style" key holds the inline CSS string.
    // A null value is a tombstone — the attribute has been removed.
    attributes: Map<string, VersionedValue<string | null>>;
    // Each class name is tracked independently.
    // false = the class was explicitly removed (LWW tombstone).
    classes: Map<string, VersionedValue<boolean>>;
    // Raw CSS the user types manually in the code editor panel.
    customStyles: Map<string, VersionedValue<string>>;
    // Children are kept in insertion order for optimistic local state.
    // After server confirmation they are re-sorted by position string.
    children: TreeNode[];
    createdUserId: string;
    createdAt: number;
    updatedUserId: string;
    updatedAt: number;
}

// ─── Builder styles ──────────────────────────────────────────────────────────
//
// Atomic styles generated by the visual editor (dropdowns, color pickers, etc.).
// Stored at the page level in DomTree.builderStyles keyed by nodeId, NOT on the
// node itself. This lets the CSS compiler iterate one flat structure instead of
// walking every node.
//
// A NodeStyle with value === undefined is a tombstone — the rule has been removed.

export interface NodeStyle {
    nodeId: string;
    property: string;
    value: string | undefined;
    pseudo: string;
    media: string;
    theme: string;
    version: number;
    seq: number;
    updatedAt: number;
}

// ─── Fonts ───────────────────────────────────────────────────────────────────

export interface TreeFont {
    id: string;
    name: string;
    contentUrl: string;
}

// ─── Tree metadata ───────────────────────────────────────────────────────────

export type TreeType = "document" | "template" | "component" | "layout";

// ─── Patches ─────────────────────────────────────────────────────────────────

export type TreePatchType =
    | "add"
    | "remove"
    | "move"
    | "updateAttribute"
    | "updateClass"
    | "updateText"
    | "updateTagName"
    | "updateComment"
    | "setBuilderStyle"
    | "removeBuilderStyle"
    | "setCustomStyles"
    | "addFont"
    | "removeFont";

export interface TreePatch {
    type: TreePatchType;
    // UUID v4 generated by the client. Used to match server broadcasts back to
    // the originating pending patch.
    clientId: string;
    nodeId: string;
    // Always 0 on outgoing patches — the server assigns the real seq.
    seq: number;
    parentId?: string;
    // Sibling hints for add/move. The server reads these to generate a LexoRank
    // position between the two siblings.
    siblingBeforeId?: string;
    siblingAfterId?: string;
    // Populated by the server on the broadcast; never set by the client.
    position?: string;
    // Full node subtree for "add" patches.
    node?: TreeNode;
    // When true on an "add" patch, the existing node with nodeId is removed first
    // before the new subtree is inserted. Mirrors the server's deleteFirst flag.
    deleteFirst?: boolean;
    propertyName?: string;
    // versionedValue carries version: 0, seq: 0 on outgoing patches.
    // The server stamps the real values on the broadcast.
    versionedValue?: VersionedValue<string | boolean | null>;
    fontName?: string;
    fontId?: string;
    // Context fields for setBuilderStyle / removeBuilderStyle — match server PatchDto names.
    pseudo?: string;
    media?: string;
    theme?: string;
    // Populated by the server on a successful "add" broadcast.
    // Flat list of every node in the added subtree with their server-assigned positions.
    affectedNodes?: TreeNode[];
    resultMessage?: string;
}

// ─── Server ack result codes ─────────────────────────────────────────────────

export type PatchAckResult =
    | "Pending"
    | "Success"
    | "VersionConflict"
    | "IdConflict"
    | "CrossIdConflict"
    | "NotFound"
    | "IndexConflict"
    | "InvalidPatch"
    | "UnknownError"
    | "CannotMoveIntoDescendant"
    | "CoexistenceNotAllowed"
    | "MaximumReached"
    | "Unauthorized"
    | "NoChange"
    | "PatchFailed";

// ─── DOM change events ────────────────────────────────────────────────────────
//
// Every tree mutation emits one DomChangeEvent describing exactly what changed.
// The iframe bridge subscribes to these and applies minimal DOM mutations —
// no full re-renders.

export type DomChangeEventType =
    | "nodeAdded"
    | "nodeRemoved"
    | "nodeMoved"
    | "nodePositionConfirmed"
    | "attributeSet"
    | "attributeRemoved"
    | "classAdded"
    | "classRemoved"
    | "textChanged"
    | "tagNameChanged"
    | "commentChanged"
    | "builderStyleSet"
    | "builderStyleRemoved"
    | "customStyleChanged"
    | "fontAdded"
    | "fontRemoved"
    // Emitted when a patch arrives with seq > tree.seq + 1.
    // The listener should call window.catchUp(pageId, tree.seq).
    | "catchUpNeeded"
    // Emitted when a local patch is rejected because a limit or structural
    // constraint was violated (e.g. MaxPageNodes, MaxAttributeCount, cycle
    // detection). LWW rejections (stale remote packets) are silent — they
    // never reach this event. Only locally originated rejections fire here.
    | "validationFailed";

export interface DomChangeEvent {
    type: DomChangeEventType;
    nodeId: string;

    // nodeAdded
    addedNode?: TreeNode;
    addedToParentId?: string;

     siblingBeforeId?: string;
    siblingAfterId?: string;
    
    // nodeMoved / nodePositionConfirmed
    newParentId?: string;
    oldParentId?: string;
    serverPosition?: string;

    // attributeSet / attributeRemoved
    attributeName?: string;
    attributeValue?: string | null;

    // classAdded / classRemoved
    className?: string;
    classActive?: boolean;

    // textChanged / commentChanged
    textValue?: string | null;
    commentValue?: string | null;

    // tagNameChanged
    tagName?: string;

    // builderStyleSet / builderStyleRemoved
    nodeStyle?: NodeStyle;

    // customStyleChanged
    customStyleKey?: string;
    customStyleValue?: string;

    // fontAdded / fontRemoved
    fontId?: string;
    fontName?: string;

    // catchUpNeeded
    currentSeq?: number;
    receivedSeq?: number;

    // validationFailed — emitted when a local patch is rejected due to a limit
    // violation or structural error (not LWW). Caller should show an error to
    // the user (e.g. "Page node limit reached", "Attribute name too long").
    validationResult?: PatchAckResult;
}

// ─── Undo / redo ─────────────────────────────────────────────────────────────

export interface UndoRecord {
    // clientIds of the forward patches, used to locate and remove this record
    // from the undo stack when the server rejects the patches.
    clientIds: string[];
    forwardPatches: TreePatch[];
    inversePatches: TreePatch[];
    // clientIds of the in-flight undo/redo patches, set by DomSession after applyAsNewPatches.
    pendingClientIds?: string[];
}

export interface ForeignChildrenEvent {
    /** The node whose undo-remove would cascade-delete these children. */
    nodeId: string;
    /** Children added by other users after the original add was captured. */
    foreignChildren: TreeNode[];
}

// ─── Patch queue ─────────────────────────────────────────────────────────────

export interface PatchQueueEntry {
    clientId: string;
    patch: TreePatch;
    enqueuedAt: number;
    retryCount: number;
}

// ─── Snapshot ────────────────────────────────────────────────────────────────

export interface TreeSnapshot {
    id: string;
    pageType: TreeType;
    version: number;
    seq: number;
    root: TreeNode;
    // Plain objects after JSON round-trip — restored to Maps during load.
    builderStyles: Record<string, NodeStyle[]>;
    fonts: Record<string, TreeFont>;
}

// ─── Tree limits ─────────────────────────────────────────────────────────────

export const TreeLimits = {
    MaxPageNodes: 2147483647,
    MaxFontCount: 50,
    MaxAttributeCount: 500,
    MaxClassCount: 50,
    MaxStylePropertyCount: 500,

    MaxTextLength: 100_000,
    MaxCommentLength: 5_000,

    MaxAttributeKeyLength: 300,
    MaxAttributeValueLength: 15_000,

    MaxClassNameLength: 100,

    MaxStylePropertyLength: 100,
    MaxStyleValueLength: 500,
    MaxStylePseudoLength: 50,
    MaxStyleMediaLength: 200,
    MaxStyleThemeLength: 50,

    MaxCustomStylesLength: 50_000,

    MaxFontNameLength: 100,
    MaxFontValueLength: 2_000,

    MaxIdLength: 128,
    MaxTagNameLength: 128,

    MaxFlattenDepth: 32,
    MaxFlattenNodes: 2000,

    MaxFontContentUrlLength: 10_000,

    MaxProjectNameLength: 200,
    MaxProjectDescriptionLength: 1000,
} as const;
